// Drawing upload widget — drag-drop zone + file picker fallback. Shows
// per-file progress, surfaces friendly errors for size/type rejections, and
// calls onUploaded(urls) once all queued files have finished (or failed).
//
// The component is purely about getting files into Firebase Storage. It does
// NOT touch part.attachedDrawings — the caller does that via the returned
// URLs. Mirrors the seam in src/shared/drawingStorage.ts (storage IO) vs
// src/shared/crm.ts (CRM state) so neither layer leaks into the other.
//
// Progress reporting note: we use uploadBytes (one-shot) rather than
// uploadBytesResumable because the 25MB cap means uploads finish quickly
// and the resumable progress events buy us little. Per-file status flips
// queued → uploading → done/error as a result; we don't render mid-upload %.

import { useCallback, useRef, useState } from 'react';
import { uploadPartDrawing, type UploadedDrawing } from '@/shared/drawingStorage';

/**
 * Caller can either:
 *   - pass a `partId` (back-compat — uploads land in /drawings/parts/$partId/),
 *   - pass an `uploader` (e.g. for the quote-level pool, which writes to
 *     /drawings/quotes/$quoteId/ via uploadQuoteAttachment).
 * Exactly one of the two is required.
 */
export interface DrawingUploadProps {
  partId?: string;
  uploader?: (file: File) => Promise<UploadedDrawing>;
  onUploaded: (urls: string[]) => void;
}

type FileStatus = 'queued' | 'uploading' | 'done' | 'error';

interface FileEntry {
  // Stable key for React — File objects don't have identity across re-renders
  // when the same File is dropped/picked twice. UUID-ish prefix + timestamp
  // is sufficient since we only need uniqueness within this widget instance.
  key: string;
  file: File;
  status: FileStatus;
  errorMessage?: string;
  result?: UploadedDrawing;
}

function makeKey(file: File): string {
  return `${file.name}-${file.size}-${file.lastModified}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function DrawingUpload({ partId, uploader, onUploaded }: DrawingUploadProps) {
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const updateEntry = useCallback((key: string, patch: Partial<FileEntry>) => {
    setEntries((prev) => prev.map((e) => (e.key === key ? { ...e, ...patch } : e)));
  }, []);

  // Resolve the per-file uploader. Custom uploader wins when both are set;
  // partId is the back-compat path.
  const resolvedUploader = useCallback(
    (file: File) => {
      if (uploader) return uploader(file);
      if (partId) return uploadPartDrawing(partId, file);
      return Promise.reject(
        new Error('DrawingUpload: must pass either `partId` or `uploader`.'),
      );
    },
    [uploader, partId],
  );

  const runUploads = useCallback(
    async (newEntries: FileEntry[]) => {
      setIsUploading(true);
      const completed: string[] = [];

      for (const entry of newEntries) {
        updateEntry(entry.key, { status: 'uploading' });
        try {
          const result = await resolvedUploader(entry.file);
          updateEntry(entry.key, { status: 'done', result });
          completed.push(result.url);
        } catch (err) {
          updateEntry(entry.key, {
            status: 'error',
            errorMessage: (err as Error).message,
          });
        }
      }

      setIsUploading(false);
      if (completed.length > 0) onUploaded(completed);
    },
    [resolvedUploader, onUploaded, updateEntry],
  );

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;
      const fresh: FileEntry[] = Array.from(fileList).map((file) => ({
        key: makeKey(file),
        file,
        status: 'queued',
      }));
      setEntries((prev) => [...prev, ...fresh]);
      void runUploads(fresh);
    },
    [runUploads],
  );

  function onDragEnter(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }

  function onDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }

  function onDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  }

  function openPicker() {
    inputRef.current?.click();
  }

  return (
    <div className="space-y-3">
      <div
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={openPicker}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPicker();
          }
        }}
        className={`border-2 border-dashed rounded p-6 text-center cursor-pointer transition-colors ${
          isDragging
            ? 'border-orange bg-orange/5'
            : 'border-line-dark hover:border-orange'
        }`}
      >
        <div className="text-3xl mb-2">📎</div>
        <div className="text-sm text-text font-semibold mb-1">
          Drop drawings here or click to pick
        </div>
        <div className="text-[11px] text-text-mute">
          PDF · drawings (PNG/JPG/SVG) · 2D CAM (DXF/DWG) · 3D (STEP/STP/P21/IGES/IGS/STL) · native CAD (SLDPRT/IPT/F3D/PRT/X_T) · 25MB max
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.dxf,.step,.stp,.iges,.igs,.png,.jpg,.jpeg,.svg"
          className="hidden"
          onChange={(e) => {
            handleFiles(e.target.files);
            // Reset so picking the same file twice in a row still fires onChange
            e.target.value = '';
          }}
        />
      </div>

      {entries.length > 0 && (
        <ul className="space-y-1.5">
          {entries.map((entry) => (
            <li
              key={entry.key}
              className="bg-paper border border-line rounded px-3 py-2 text-xs"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1 font-mono truncate" title={entry.file.name}>
                  {entry.file.name}
                </div>
                <StatusPill entry={entry} />
              </div>
              {entry.status === 'error' && entry.errorMessage && (
                <div className="text-[11px] text-orange mt-1 break-words">
                  {entry.errorMessage}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {isUploading && (
        <div className="text-[11px] text-text-mute italic">Uploading…</div>
      )}
    </div>
  );
}

function StatusPill({ entry }: { entry: FileEntry }) {
  switch (entry.status) {
    case 'queued':
      return (
        <span className="text-[11px] text-text-mute">queued</span>
      );
    case 'uploading':
      return (
        <span className="text-[11px] text-blue animate-pulse">uploading…</span>
      );
    case 'done':
      return (
        <span className="text-[11px] text-green font-semibold">✓ done</span>
      );
    case 'error':
      return (
        <span className="text-[11px] text-orange font-semibold">⚠ failed</span>
      );
  }
}
